import { syncRef, tryOnScopeDispose, useFetch, type SyncRefOptions } from '@vueuse/core'
import { computed, ref, shallowRef, toRef, type Ref, type ShallowRef } from 'vue'

export function useUnsavedRef<T>(existing: Ref<T>) {
  const unsaved = ref(false)

  const modelValue = computed({
    get(): T {
      return existing.value
    },
    set(newValue: T): void {
      existing.value = newValue
      unsaved.value = true
    },
  })

  return {
    unsaved,
    modelValue,
  }
}

export function useSyncedShallowPropRef<T extends object, K extends keyof T>(props: T, key: K): ShallowRef<T[K]> {
  const source = toRef(props, key)
  const local = shallowRef(props[key]) as ShallowRef<T[K]>

  const syncOptions = [{ direction: 'ltr' }] as [SyncRefOptions<T[K], T[K], 'ltr'>]

  syncRef(source, local, ...syncOptions)
  return local
}

type VersionJson = {
  date: string
  version?: string
}

export function useVersionData() {
  return useFetch(import.meta.env.BASE_URL + '_version.json', {
    immediate: !import.meta.env.SSR,
  }).json<VersionJson>()
}

export function useVersionCheck(currentVersion: string | undefined): Ref<boolean> {
  const updateAvailable = ref(false)
  let intervalId: ReturnType<typeof setInterval> | undefined

  // Skip version checking during SSR
  if (import.meta.env.SSR) {
    return updateAvailable
  }

  if (!currentVersion) {
    // cannot check for updates if we don't know the current version (or are in dev mode).
    // this becomes a no-op, and the ref will always be false.
    return updateAvailable
  }

  const checkVersion = async () => {
    try {
      const response = await fetch(import.meta.env.BASE_URL + '_version.json')

      if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
        console.warn(
          'version check failed: invalid response',
          response.status,
          response.statusText,
          response.headers.get('content-type'),
        )
        return
      }

      const { version } = (await response.json()) as VersionJson

      if (version !== currentVersion) {
        console.info(`new version detected: ${version} (current: ${currentVersion})`)
        updateAvailable.value = true
        clearInterval(intervalId)
        intervalId = undefined
      }
    } catch (error) {
      console.error('version check failed:', error)
    }
  }

  console.debug('installing version check watcher, current version:', currentVersion)
  intervalId = setInterval(checkVersion, 30 * 60 * 1000)
  tryOnScopeDispose(() => clearInterval(intervalId))

  return updateAvailable
}
